import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FormOutlined, UserOutlined } from "@ant-design/icons";
import {
  Avatar,
  Button,
  Input,
  List,
  Popover,
  Select,
  Space,
  Tooltip,
  Typography,
} from "antd";
import { PATTERNS } from "constants/appConstant";
import parsePhoneNumber from "libphonenumber-js";
import { debounce } from "lodash";
import { AuthInfo } from "stores/slices/auth";
import {
  doCreateConversation,
  setConversationSelected,
} from "stores/slices/conversation";
import { doGetCustomerPage, isSaving } from "stores/slices/customer";
import { doGetAddressPage } from "stores/slices/numbers";

import AppButton from "components/button";

import styles from "./styles.module.scss";

const { Title, Text } = Typography;
const AddNewConversation = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const authInfo = useSelector(AuthInfo);

  const [isOpenAddPopup, setIsOpenAddPopup] = useState(false);
  const [inputText, setInputText] = useState("");
  const [isValidPhoneNumber, setIsValidPhoneNumber] = useState(false);
  const [allCustomerList, setAllCustomerList] = useState([]);
  const [pageCustomer, setPageCustomer] = useState(null);
  const [serviceList, setServiceList] = useState([]);
  const [isLoadingService, setIsLoadingService] = useState(false);

  const [searchParams, setSearchParams] = useState({
    sortFieldCustomer: "createdTime",
    sortDirection: "DESC",
    pageNumber: 1,
    pageSize: 10,
    searchKeyPhoneNumber: "",
  });

  const handleOpenChange = newOpen => {
    setIsOpenAddPopup(newOpen);
    setSearchParams({
      ...searchParams,
      searchKeyPhoneNumber: "",
      searchKeyName: "",
    });
  };

  const handleSearchCustomer = useCallback(
    inputText => {
      let newSearchParams;
      const isNumber = !isNaN(inputText) && inputText !== "";
      if (isNumber) {
        newSearchParams = {
          ...searchParams,
          searchKeyPhoneNumber: inputText,
          searchKeyName: "",
        };
      } else {
        newSearchParams = {
          ...searchParams,
          searchKeyName: inputText,
          searchKeyPhoneNumber: "",
        };
      }

      setSearchParams(newSearchParams);
    },
    [inputText]
  );

  const handleGetCustomerPage = async () => {
    const { payload } = await dispatch(doGetCustomerPage(searchParams));
    setAllCustomerList([...payload.content]);
    setPageCustomer({
      pageNumber: payload.pageNumber,
      totalPages: payload.totalPages,
    });
  };

  const handleLoadMoreCustomerPage = async () => {
    const params = {
      ...searchParams,
      pageNumber: pageCustomer.pageNumber + 1,
    };
    const { payload } = await dispatch(doGetCustomerPage(params));
    if (payload && payload?.content?.length > 0) {
      setAllCustomerList(preCustomerList => [
        ...preCustomerList,
        ...payload.content,
      ]);
    }
    setPageCustomer({
      pageNumber: payload.pageNumber,
      totalPages: payload.totalPages,
    });
  };

  const handleGetServicePage = async () => {
    const params = {
      pageNumber: 1,
      pageSize: 10,
      workspaceId: authInfo && authInfo.workspaceId,
      sortField: "createdTime",
      sortDirection: "DESC",
      statuses: ["ASSIGNED"],
    };

    setIsLoadingService(true);
    const { payload } = await dispatch(doGetAddressPage(params));
    const services = payload && payload.content;
    if (services) {
      // const serviceOptions = services.map(service => ({
      //   label: service?.address,
      //   value: service?.id,
      // }));
      setServiceList([...services]);
      setIsLoadingService(false);
    } else {
      setServiceList([]);
    }
    setIsLoadingService(false);
  };

  useEffect(() => {
    if (isOpenAddPopup) {
      handleGetServicePage();
      handleGetCustomerPage();
    }
  }, [isOpenAddPopup, searchParams]);

  return (
    <>
      <Tooltip title={t("inbox.conversation.startNewConversation")}>
        <Popover
          open={isOpenAddPopup}
          onOpenChange={handleOpenChange}
          content={
            <AddNewConversationPopup
              setIsOpenAddPopup={setIsOpenAddPopup}
              allCustomerList={allCustomerList}
              handleLoadMoreCustomerPage={handleLoadMoreCustomerPage}
              handleSearchCustomer={handleSearchCustomer}
              isValidPhoneNumber={isValidPhoneNumber}
              setIsValidPhoneNumber={setIsValidPhoneNumber}
              setInputText={setInputText}
              pageCustomer={pageCustomer}
              services={serviceList}
              isLoadingService={isLoadingService}
            />
          }
          placement="bottomLeft"
          trigger="click"
          arrow={false}
          destroyTooltipOnHide={true}
        >
          <Button
            icon={<FormOutlined />}
            type="text"
            shape="circle"
            size="large"
          />
        </Popover>
      </Tooltip>
    </>
  );
};

const AddNewConversationPopup = ({
  setIsOpenAddPopup,
  allCustomerList,
  handleLoadMoreCustomerPage,
  handleSearchCustomer,
  isValidPhoneNumber,
  setIsValidPhoneNumber,
  setInputText,
  pageCustomer,
  // isLoadingService,
  services,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isLoading = useSelector(isSaving);

  const serviceList = useMemo(() => {
    if (services?.length > 0) {
      return services.map(service => ({
        label: service?.address,
        value: service?.id,
      }));
    } else {
      return [];
    }
  }, [services]);

  const [serviceIdSelected, setServiceIdSelected] = useState("");

  const handleInputTextChange = debounce(e => {
    setInputText(e.target.value.trim());
    handleSearchCustomer(e.target.value.trim());
    const isValid = checkValidPhoneNumber(e.target.value.trim());
    setIsValidPhoneNumber(isValid);
  }, 800);

  const checkValidPhoneNumber = phone => {
    let phoneNumber = phone;
    if (
      PATTERNS.PHONE_NUMBER_LENGTH.test(phone) &&
      PATTERNS.ZERO_ONLY.test(phone)
    ) {
      const validPhoneNumber = parsePhoneNumber(phoneNumber);
      if (validPhoneNumber && validPhoneNumber.isPossible()) {
        if (validPhoneNumber.country && validPhoneNumber.isValid()) {
          const newValue = validPhoneNumber.formatInternational();
          return newValue.replaceAll(" ", "").replaceAll("+", "");
        }
      } else {
        phoneNumber = phone.includes("+") ? phone : `+${phoneNumber}`;
        const validContactNumber = parsePhoneNumber(phoneNumber);
        if (validContactNumber && validContactNumber.isPossible()) {
          if (validContactNumber.country && validContactNumber.isValid()) {
            const newValue = validContactNumber.formatInternational();
            return newValue.replaceAll(" ", "").replaceAll("+", "");
          } else {
            return false;
          }
        } else {
          const validPhoneNumber2 = parsePhoneNumber(phone, "AU");
          if (validPhoneNumber2 && validPhoneNumber2.isPossible()) {
            if (validPhoneNumber2.country && validPhoneNumber2.isValid()) {
              const newValue = validPhoneNumber2.formatInternational();
              return newValue.replaceAll(" ", "").replaceAll("+", "");
            } else {
              return false;
            }
          } else {
            const validContactNumber2 = parsePhoneNumber(phoneNumber, "AU");
            if (validContactNumber2 && validContactNumber2.isPossible()) {
              if (
                validContactNumber2.country &&
                validContactNumber2.isValid()
              ) {
                const newValue = validContactNumber2.formatInternational();
                return newValue.replaceAll(" ", "").replaceAll("+", "");
              } else {
                return false;
              }
            }
          }
        }
      }
    } else {
      return false;
    }
  };

  const handleChange = value => {
    setServiceIdSelected(value);
  };

  const handleGetConversation = async phoneNumber => {
    if (phoneNumber && serviceIdSelected) {
      // Check if a conversation is found, redirect to the conversation or create a new conversation and redirect to the conversation.
      const { payload } = await dispatch(
        doCreateConversation({
          phoneNumber: phoneNumber,
          serviceId: serviceIdSelected,
        })
      );
      if (payload && payload.status === 200) {
        const conversation = payload?.data;
        if (conversation && conversation.id) {
          await setIsOpenAddPopup(false);
          await navigate(`/inbox/${conversation.id}`);
          await dispatch(setConversationSelected(conversation));
        }
      }
    }
  };

  useEffect(() => {
    // Set first service when user not selected service
    if (serviceList && serviceList.length > 0) {
      setServiceIdSelected(serviceList[0]?.value);
    }
  }, [serviceList]);

  return (
    <>
      <div className={styles.popupContainer}>
        <Title level={4}>{t("inbox.conversation.startNewConversation")}</Title>
        <div>
          <Text className={styles.textLabel}>
            {t("inbox.conversation.from")}
          </Text>
          {serviceList?.length > 0 && (
            <Select
              style={{
                width: "100%",
              }}
              size="large"
              allowClear
              defaultValue={serviceList[0]}
              placeholder={"Select service"}
              disabled={!isValidPhoneNumber || allCustomerList?.length > 0}
              onChange={handleChange}
              options={serviceList}
            />
          )}
          {serviceList?.length === 0 && (
            <Select
              style={{
                width: "100%",
              }}
              size="large"
              allowClear
              placeholder={"Select service"}
              disabled={serviceList?.length === 0}
              options={serviceList}
            />
          )}
        </div>

        <div
          style={{
            marginTop: 12,
          }}
        >
          <Text className={styles.textLabel}>{t("inbox.conversation.to")}</Text>
          <Space.Compact
            style={{
              width: "100%",
            }}
          >
            <Input
              size="large"
              placeholder={t("inbox.conversation.searchNameOrNumber")}
              onChange={handleInputTextChange}
            />
            <AppButton
              label={t("inbox.conversation.start")}
              color={"primary"}
              size={"large"}
              type="primary"
              onClick={() => handleGetConversation(isValidPhoneNumber)}
              disabled={!isValidPhoneNumber}
              style={{
                minWidth: "max-content",
              }}
            />
          </Space.Compact>
        </div>

        <div style={{ marginTop: 24 }}>
          <Title level={5}>{t("inbox.conversation.allCustomers")}</Title>
          <div id="scrollDiv">
            <InfiniteScroll
              dataLength={allCustomerList && allCustomerList.length}
              hasMore={pageCustomer?.pageNumber < pageCustomer?.totalPages}
              next={handleLoadMoreCustomerPage}
              scrollableTarget="scrollDiv"
              height={400}
              className="scroll"
            >
              <List
                itemLayout={"horizontal"}
                dataSource={allCustomerList}
                loading={isLoading}
                className="customer-list"
                renderItem={customer => (
                  <>
                    <div className={styles.customerContainer}>
                      <div className={styles.customerInfo}>
                        <div className={styles.avatarContainer}>
                          {customer?.profileImage ? (
                            <Avatar
                              size={"large"}
                              src={customer?.profileImage}
                            />
                          ) : (
                            <Avatar size={"large"} icon={<UserOutlined />} />
                          )}
                        </div>
                        <Text className={styles.customerName}>
                          {customer?.firstName || customer?.lastName
                            ? `${customer?.firstName || ""} ${
                                customer?.lastName || ""
                              }`
                            : customer?.contact?.phoneNumber || ""}
                        </Text>
                      </div>
                      <div className={styles.startBtn}>
                        <AppButton
                          label={t("inbox.conversation.start")}
                          color={"primary"}
                          size={"small"}
                          type="primary"
                          onClick={() => {
                            handleGetConversation(
                              customer?.contact?.phoneNumber
                            );
                          }}
                          style={{
                            minWidth: "max-content",
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}
              />
            </InfiniteScroll>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddNewConversation;
